/**
 * sessionInsights.js — Session-level passive insight generation.
 *
 * Takes active operational signals and produces at most 1 calm,
 * pastoral insight for display in the KeviaInsightCard.
 *
 * Design constraints (FASE 18.13 — UX Calmness):
 *  - Max 1 insight visible
 *  - Non-invasive — never block or distract
 *  - Pastoral language, not analytics
 *  - Only shown when confidence is medium or higher
 */

import { SIGNALS } from "../kevia-signals";

/* ─── Insight templates per signal ────────────────────────────────────── */

const INSIGHT_MAP = {
  [SIGNALS.FOLLOW_UP_RISK]: (signal) => {
    const names = signal.relatedEntities.slice(0, 2).join(", ");
    return {
      id:          "follow-up-risk",
      explanation: names
        ? `Has visitado ${names} sin registrar acción de seguimiento.`
        : "Hay entidades pastorales recientes sin seguimiento registrado.",
      mood:        "reflective",
      confidence:  signal.confidence,
    };
  },
  [SIGNALS.INACTIVE_FAMILY_PATTERN]: (signal) => ({
    id:          "inactive-families",
    explanation: "Varias familias revisadas en esta sesión — ¿hay seguimiento pendiente?",
    mood:        "pastoral-care",
    confidence:  signal.confidence,
  }),
  [SIGNALS.PRAYER_FOCUS]: (signal) => ({
    id:          "prayer-focus",
    explanation: "Sesión enfocada en oración. ¿Hay peticiones que necesiten seguimiento pastoral?",
    mood:        "reflective",
    confidence:  signal.confidence,
  }),
  [SIGNALS.MEMBER_RECOVERY_FLOW]: (signal) => {
    const names = signal.relatedEntities.slice(0, 1).join("");
    return {
      id:          "member-recovery",
      explanation: names
        ? `Seguimiento activo con ${names}.`
        : "Hay un flujo de seguimiento pastoral activo en esta sesión.",
      mood:        "pastoral-care",
      confidence:  signal.confidence,
    };
  },
  [SIGNALS.AUDIT_INVESTIGATION]: () => ({
    id:          "audit-active",
    explanation: "Revisando registros del sistema. Puedo ayudarte a interpretar los eventos.",
    mood:        "neutral",
    confidence:  "high",
  }),
  [SIGNALS.HIGH_VISIT_ACTIVITY]: () => ({
    id:          "visit-activity",
    explanation: "Alta actividad de visitas en esta sesión. ¿Necesitas planificar seguimientos?",
    mood:        "neutral",
    confidence:  "medium",
  }),
};

const CONFIDENCE_ORDER = { high: 3, medium: 2, low: 1 };

/**
 * Generates at most 1 passive insight from the active signal list.
 * Returns null if no relevant insight is available.
 *
 * @param {Signal[]} activeSignals — from getActiveSignals()
 * @returns {{ id, explanation, mood, confidence }|null}
 */
export function generateSessionInsight(activeSignals) {
  if (!activeSignals || activeSignals.length === 0) return null;

  for (const signal of activeSignals) {
    const template = INSIGHT_MAP[signal.id];
    if (!template) continue;
    // Only surface medium/high confidence insights — low is too noisy
    if ((CONFIDENCE_ORDER[signal.confidence] ?? 0) < 2) continue;
    return template(signal);
  }

  return null;
}
